import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FormContainer from '../../components/ui/FormContainer';
import Button from '../../components/ui/Button';
import ConfirmationHeader from './components/ConfirmationHeader';
import DealInformationCard from './components/DealInformationCard';
import UploadedDocumentsCard from './components/UploadedDocumentsCard';
import InvestmentSummaryCard from './components/InvestmentSummaryCard';
import { loadFormData } from '../../utils/onboardingApi';

// Deal Confirmation Overview
// Reads form data from API and displays for confirmation
const DealConfirmationOverview = () => {
  const navigate = useNavigate();

  const [dealData, setDealData] = useState(null);
  const [documentsData, setDocumentsData] = useState([]);
  const [investmentData, setInvestmentData] = useState({
    targetReturn: '',
    investmentTerm: '',
    minimumInvestment: '',
    targetCloseDate: ''
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load form data from API on mount
  useEffect(() => {
    async function loadData() {
      try {
        // Load deal info and documents from API
        const dealInfo = await loadFormData('deal-info-1');
        const docs = await loadFormData('documents');

        if (!dealInfo?.dealFundName) {
          navigate('/deal-information-form-step-1');
          return;
        }

        // Transform for DealInformationCard
        setDealData({
          dealName: dealInfo?.dealFundName || '',
          assetType: dealInfo?.assetType || '',
          dealStructure: dealInfo?.dealFundStructure || '',
          description: dealInfo?.briefDescription || ''
        });

        // Build documents array
        const docSource = {
          ppm: docs?.ppm || null,
          pitchDeck: docs?.pitchDeck || null,
          proformaXls: docs?.proformaXls || null
        };
        const documentsArray = Object.entries(docSource).reduce((acc, [key, value]) => {
          if (value && value.name) {
            const ext = value.name.split('.').pop().toLowerCase();
            acc.push({
              id: key,
              label: key === 'ppm' ? 'PPM' : key === 'pitchDeck' ? 'Pitch Deck' : 'Proforma XLS',
              fileName: value.name,
              type: ext === 'pdf' ? 'pdf' : (ext === 'xlsx' || ext === 'xls') ? 'xlsx' : 'file'
            });
          }
          return acc;
        }, []);
        setDocumentsData(documentsArray);

        // Initialize investment data
        setInvestmentData(prev => ({
          targetReturn: dealInfo?.targetReturn || prev.targetReturn || '15-18% IRR',
          investmentTerm: dealInfo?.investmentTerm || prev.investmentTerm || '5 years',
          minimumInvestment: dealInfo?.minimumInvestment || prev.minimumInvestment || '$50,000',
          targetCloseDate: dealInfo?.targetCloseDate || prev.targetCloseDate || ''
        }));
      } catch (e) {
        console.error('Failed to load form data:', e);
        navigate('/deal-information-form-step-1');
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [navigate]);

  // Auto-save investment edits (debounced) back to flat dealFormData
  useEffect(() => {
    const timeout = setTimeout(() => {
      try {
        const raw = localStorage.getItem('dealFormData');
        const existing = raw ? JSON.parse(raw) : {};
        const merged = {
          ...existing,
          targetReturn: investmentData?.targetReturn,
          investmentTerm: investmentData?.investmentTerm,
          minimumInvestment: investmentData?.minimumInvestment,
          targetCloseDate: investmentData?.targetCloseDate,
          lastSaved: new Date().toISOString()
        };
        localStorage.setItem('dealFormData', JSON.stringify(merged));
      } catch (e) {
        console.error('Failed to persist investment data:', e);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [investmentData]);

  // Navigation handlers
  const handleBack = () => navigate('/document-upload-step-2');
  const handleEditDeal = () => navigate('/deal-information-form-step-1');
  const handleEditDocuments = () => navigate('/document-upload-step-2');

  const handleEditInvestment = (updatedData) => {
    setInvestmentData(prev => ({
      targetReturn: updatedData?.targetReturn || prev.targetReturn,
      investmentTerm: updatedData?.investmentTerm || prev.investmentTerm,
      minimumInvestment: updatedData?.minimumInvestment || prev.minimumInvestment,
      targetCloseDate: updatedData?.targetFundingDate || updatedData?.targetCloseDate || prev.targetCloseDate
    }));
  };

  const handleSaveAndExit = () => {
    try {
      const raw = localStorage.getItem('dealFormData');
      const existing = raw ? JSON.parse(raw) : {};
      const merged = {
        ...existing,
        // Ensure latest core deal fields persist (in case earlier pages changed)
        dealFundName: dealData?.dealName,
        assetType: dealData?.assetType,
        dealFundStructure: dealData?.dealStructure,
        briefDescription: dealData?.description,
        targetReturn: investmentData?.targetReturn,
        investmentTerm: investmentData?.investmentTerm,
        minimumInvestment: investmentData?.minimumInvestment,
        targetCloseDate: investmentData?.targetCloseDate
      };
      localStorage.setItem('dealFormData', JSON.stringify(merged));
    } catch (e) {
      console.error('Failed final save on exit:', e);
    }
    navigate('/deals-dashboard-empty-state');
  };

  const handleConfigureIR = () => {
    try {
      const raw = localStorage.getItem('dealFormData');
      const existing = raw ? JSON.parse(raw) : {};
      const merged = {
        ...existing,
        dealFundName: dealData?.dealName,
        assetType: dealData?.assetType,
        dealFundStructure: dealData?.dealStructure,
        briefDescription: dealData?.description,
        targetReturn: investmentData?.targetReturn,
        investmentTerm: investmentData?.investmentTerm,
        minimumInvestment: investmentData?.minimumInvestment,
        targetCloseDate: investmentData?.targetCloseDate
      };
      localStorage.setItem('dealFormData', JSON.stringify(merged));
    } catch (e) {
      console.error('Failed save before IR configure:', e);

    }
    navigate('/brand-guidelines-setup');
  };

  if (!dealData) {
    return <FormContainer><div>Loading...</div></FormContainer>;
  }

  return (
    <FormContainer maxWidth="max-w-4xl">
      <div className="space-y-8">
        <ConfirmationHeader onBack={handleBack} />

        <div className="space-y-6">
          <DealInformationCard dealData={dealData} onEdit={handleEditDeal} />
          <UploadedDocumentsCard documentsData={documentsData} onEdit={handleEditDocuments} />
          <InvestmentSummaryCard investmentData={investmentData} onEdit={handleEditInvestment} />
        </div>

        <div className="flex justify-between items-center pt-6">
          {/* <Button
            onClick={handleSaveAndExit}
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Save & Exit
          </Button> */}
          <Button
            onClick={handleConfigureIR}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            size="lg"
          >
            Brand Setup
          </Button>
        </div>
      </div>
    </FormContainer>
  );
};

export default DealConfirmationOverview;
